/**
 * useShellDomainTable - the table's record and its turn: the current tab,
 * undo, the three writers every other hook is handed, sheet rolls, the turn
 * tracker and its prompts, overheats and the shared effect presets.
 *
 * A domain hook: it reads what it needs from the shell scope `s` and returns
 * what it declares (see DeathWatchSimulator). Hook order unchanged.
 * Extracted from DeathWatchSimulator.
 */
import useShellFight from './useShellFight';
import useShellStory from './useShellStory';
import useShellStealth from './useShellStealth';
import { useCallback, useMemo, useRef } from 'react';
import { outranking } from '../engine/battlefield/tokens';
import * as multiplayer from '../multiplayer';
import { isMoveLineOf } from '../engine/moveBeat';
import useShellWriters from './useShellWriters';
import useHistory from './useHistory';
import useShellOverheat from './useShellOverheat';
import useShellRollActions from './useShellRollActions';
import useHideReveal from './useHideReveal';
import useStealthContacts from './useStealthContacts';
import useShellTurnTracker from './useShellTurnTracker';
import useShellEffectPresets from './useShellEffectPresets';

/**
 * An undone charge puts the charger back (2026-10-21): the run leaves the starting token here for the next undo
 * snapshot, and the undo writes it back - above whatever rev the token has reached since, through the hidden-aware
 * writer, or every screen would throw the old place away as stale. And it takes the run's own move line out of the
 * log (`dropLine`): that one entry, by its beat (the charger's move at the run's start), nothing written meanwhile.
 */
function useChargeUndo({ tokens, writeToken, setRoster, sessionId }) {
  const chargeOriginRef = useRef(null);
  const tokensNow = useRef(tokens);
  tokensNow.current = tokens;
  const restoreTokens = useCallback(
    (list) => {
      for (const t of list) {
        const now = tokensNow.current?.[t.id];
        writeToken({ ...t, rev: outranking(now?.rev ?? t.rev) }, { immediate: true });
      }
    },
    [writeToken],
  );
  // The run's own move line, and nothing else: off this screen's column now, and off the server's log.
  const dropLine = useCallback(
    (line) => {
      setRoster((prev) => prev.map((c) => (c.id === line.column ? withoutLine(c, line) : c)));
      if (sessionId) multiplayer.deleteMoveLine(sessionId, line).catch(console.error);
    },
    [setRoster, sessionId],
  );
  return { chargeOriginRef, restoreTokens, dropLine };
}

/** A combatant without one run's move line - a new object, as every roster change is (engine/moveBeat). */
function withoutLine(c, line) {
  const log = (c.narrativeLog ?? []).filter((e) => !isMoveLineOf(e.beat, line));
  return log.length === (c.narrativeLog ?? []).length ? c : { ...c, narrativeLog: log };
}

/**
 * The current tab, undo and the writers. Length: the history's argument
 * record (declarative).
 */
function useShellRecord(s) {
  const { roster, setRoster, appendNarrative, activeTab, addNarrativeRef, tabs, surfaceEffectsRef } = s;
  const { syncTargetToFirestore, syncNarrativeToFirestore } = s;

  const currentTab = useMemo(() => tabs.find((t) => t.id === activeTab) || tabs[0], [tabs, activeTab]);
  const charge = useChargeUndo(s);
  const { chargeOriginRef } = charge;
  const history = useHistory(historyArgs(s, charge));
  const { pushHistory, capturePreActionSnapshot, doUndo, canUndo, historyTrack, resetHistory } = history;
  const undoSaving = history.saving;

  // ---- Core writers (every other hook is handed these) ----
  // updateTab guards against storing the ground; see `hooks/useShellWriters`.
  const { updateTab, addNarrative, addRollNarrative } = useShellWriters({
    roster,
    setRoster,
    activeTab,
    appendNarrative,
    syncTargetToFirestore,
    syncNarrativeToFirestore,
    surfaceEffectsRef,
    addNarrativeRef,
  });
  return {
    currentTab,
    chargeOriginRef,
    pushHistory,
    capturePreActionSnapshot,
    doUndo,
    canUndo,
    undoSaving,
    historyTrack,
    resetHistory,
    updateTab,
    addNarrative,
    addRollNarrative,
  };
}

/**
 * Undo's wiring, off the shell scope: what it snapshots, the shared slice it syncs through, and the charge's token.
 * Length: one argument record (declarative).
 */
function historyArgs(s, { chargeOriginRef, restoreTokens, dropLine }) {
  const { sync, direction, isGm, sessionId } = s;
  return {
    sessionId,
    playerId: s.playerId,
    // Undo works on the STATE half only - a sheet edit must survive Ctrl+Z. The shared slice:
    // the shared history must never hold a hidden figure (ADR 2026-10-07), so undo leaves one alone.
    roster: s.sharedRoster,
    restoreStates: s.restoreStates,
    activeTab: s.activeTab,
    setActiveTab: s.setActiveTab,
    setPendingAttack: s.setPendingAttack,
    isLocked: s.isLocked,
    clearAllPendingDefences: s.clearAllPendingDefences,
    clearPendingDefencesSince: s.clearPendingDefencesSince,
    syncRefs: {
      sharedHistoryState: sync.sharedHistoryState,
      setSharedHistoryState: sync.setSharedHistoryState,
      sharedHistoryCacheRef: sync.sharedHistoryCacheRef,
      undoRedoBusyRef: sync.undoRedoBusyRef,
      lastTgtJsonRef: sync.lastTgtJsonRef,
      tgtSyncTimerRef: sync.tgtSyncTimerRef,
      narrativeQueueRef: sync.narrativeQueueRef,
      narrativeFlushTimerRef: sync.narrativeFlushTimerRef,
    },
    turnTrackerState: sync.turnTrackerState,
    // The GM's frame rides in each snapshot; only a seat that may write it gets to put one back.
    frame: direction.frame,
    imposeFrame: isGm || !sessionId ? direction.impose : null,
    // The kill-teams' pools ride too (Squad Mode): a spend alone is an entry, and its undo puts the pool back.
    cohesion: s.killTeams?.cohesion ?? null,
    imposeCohesion: s.killTeams?.impose ?? null,
    setTurnTrackerState: sync.setTurnTrackerState,
    syncTurnTrackerToFirestore: sync.syncTurnTrackerToFirestore,
    chargeOriginRef,
    restoreTokens,
    dropLine,
  };
}

/**
 * Sheet rolls, the turn tracker, overheats and effect presets. Length: the
 * hooks' argument and return records (declarative).
 */
function useShellTurn(s) {
  const {
    roster,
    actor,
    updateCombatantState,
    activeTab,
    sessionId,
    playerId,
    sync,
    permissions,
    isLocked,
    unnaturalDosMode,
    showActionEconomy,
    roundCountdown,
    stackPolicy,
    protagonistRules,
    tabs,
    initiativeModFor,
    actorProjection,
    currentTab,
    pushHistory,
    capturePreActionSnapshot,
    historyTrack,
    updateTab,
    addNarrative,
    addRollNarrative,
  } = s;

  // Sheet rolls from both panels, and each side's xN badge overrides.
  const {
    rollActions,
    defenderRollActions,
    attackerUnnaturalOverrides,
    onAttackerUnnaturalOverride,
    defenderUnnaturalOverrides,
    onDefenderUnnaturalOverride,
  } = useShellRollActions({ actor, currentTab, unnaturalDosMode, addRollNarrative });

  // ---- Turn tracker, its prompts and the action economy ----
  const {
    turnTracker,
    sheetForCombatant,
    spendTurnRef,
    resetActionEconomy,
    actionEconomy,
    endSuggested,
    pendingTurnEffect,
    handleTurnEffectChoice,
  } = useShellTurnTracker({
    tabs,
    roster,
    actor,
    isLocked,
    sync,
    permissions,
    capturePreActionSnapshot,
    historyTrack,
    updateTab,
    addNarrative,
    roundCountdown,
    stackPolicy,
    protagonistRules,
    unnaturalDosMode,
    initiativeModFor,
    showActionEconomy,
    hidden: s.hidden,
    // The GM's screen repairs the order (an Object in it): one writer (2026-10-26).
    isGm: s.isGm,
    // Squad Mode's turn starts. Through the verbs ref: they are declared in a later domain (People).
    onTurnStart: (id) => void s.killTeamVerbs?.current?.turnStart(id),
  });

  // ---- Overheats prompt (v8.198.1) - drop it or hold on; see the hook ----
  const { pendingOverheat, handleOverheatChoice, handleOverheatDetected } = useShellOverheat({
    actor,
    actorProjection,
    activeTab,
    stackPolicy,
    roundCountdown,
    updateCombatantState,
    addNarrative,
    capturePreActionSnapshot,
    pushHistory,
  });

  // ---- Effect presets: the table's shared palette ----
  const { effectPresets, handleSaveEffectPreset } = useShellEffectPresets(sessionId, playerId);

  return {
    rollActions,
    defenderRollActions,
    attackerUnnaturalOverrides,
    onAttackerUnnaturalOverride,
    defenderUnnaturalOverrides,
    onDefenderUnnaturalOverride,
    turnTracker,
    sheetForCombatant,
    spendTurnRef,
    resetActionEconomy,
    actionEconomy,
    endSuggested,
    pendingTurnEffect,
    handleTurnEffectChoice,
    pendingOverheat,
    handleOverheatChoice,
    handleOverheatDetected,
    effectPresets,
    handleSaveEffectPreset,
  };
}

/**
 * @param {object} s - the shell scope (everything the map domain and those
 *   before it declared).
 * @returns {object} `currentTab`, the history verbs, `updateTab` /
 *   `addNarrative` / `addRollNarrative`, the roll actions, the turn tracker
 *   and its economy, the overheat prompt, the effect presets, and the
 *   fight's verbs (`direction` with its phase change wrapped, `startFight`,
 *   `fightSides`, `mapModeRequest`), and `hideReveal` (the GM's hide and reveal).
 */
export default function useShellDomainTable(s) {
  const record = useShellRecord(s);
  const turn = useShellTurn({ ...s, ...record });
  // Changing the phase (and what it clears), and the fuller door into a
  // fight. Here, as soon as the tracker exists: it REPLACES `direction` in
  // the scope with one whose `setPhase` clears the scene's promises, and
  // every later domain (the scenes' cut, the header) must see that one.
  const fight = useShellFight({ ...s, ...record, ...turn });
  // Clocks, the board, and which panel the right column shows - see the hook.
  const story = useShellStory({ ...s, ...record, ...turn, ...fight });
  // Stealth's ladder, the opposition and the director's verbs - see the hook.
  const stealth = useShellStealth({ ...s, ...record, ...fight });
  // What the party knows of the figures it cannot see: the GM's levels, published - see the hook.
  const contacts = useStealthContacts({ ...s, ...record, ...fight, ...stealth });
  // The GM's hide and reveal, once the order and Undo exist - see the hook. A reveal forgets the figure's contact
  // level.
  const hideReveal = useHideReveal({ ...s, ...record, ...turn, ...contacts });
  return { ...record, ...turn, ...fight, ...story, ...stealth, ...contacts, hideReveal };
}
